import React, {
  BaseSyntheticEvent,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import SubHeader from "../layout/subheader";
import "../shared/markdown/markdown.css";
import { useLocation } from "react-router";
import ErrorDialog from "../shared/errorDialog";
import { TaskbarItem } from "../interfaces";
import { TaskbarContext } from "../shared/taskbar-context";
import DefaultDialog from "../shared/defaultDialog";
import { noVersionFound } from "../shared/markdown/noVersionFound";
import { noDataFound } from "../shared/markdown/noDataFound";
import { InlineTableOfContentsContext } from "../shared/InlineTableOfContentsProvider";
import { useDispatch } from "react-redux";
import { off, spi } from "../features/spinner/spinnerSlice";
import MarkdownView from "../shared/markdown/MarkdownView";
import VersionView from "../shared/VersionView";

const DownloadPage = (): JSX.Element => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { setTaskbarElements } = useContext(TaskbarContext);
  const repoName = useRef<string | undefined>();
  const [softwareProducts, setSoftwareProducts] = useState<any>();
  const [displayedMarkdown, setDisplayedMarkdown] = useState("");
  const [errorDialog, setErrorDialog] = useState<string | undefined>();
  const [downloadDialog, setDownloadDialog] = useState(false);
  const [showDetailVersion, setShowDetailVersion] = useState<{
    show: boolean;
    versions?: string[];
    versionText: { version: string; versionText: string };
  }>({
    show: true,
    versionText: { version: "", versionText: "" },
    versions: [],
  });

  const { setData, triggerOpen, selectedItem, openView, moveToItem } =
    useContext<any>(InlineTableOfContentsContext);

  // data = "tst";

  useEffect(() => {
    if (!openView) {
      triggerOpen(false);
    }
    if (!selectedItem) return;
    scroll(selectedItem);
    moveToItem(undefined);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, displayedMarkdown, openView]);

  const readmeMarkdown = useRef("");
  const versionMarkdown = useRef("");
  const taskbarElements = useRef<Array<TaskbarItem>>([
    {
      id: "download",
      text: "Download",
      icon: "icon-download-dark",
      action: "event",
      target: "event_download",
    },
    { type: "empty" },
    {
      id: "documentation",
      text: "Documentation",
      icon: "icon-recipes-dark",
      action: "event",
      target: "event_documentation",
    },
  ]);

  const tableOfcontentsButton = {
    id: "summary",
    text: "Table of contents",
    icon: "icon-recipes-dark",
    action: "event",
    target: "event_summary",
  };

  const url = location.pathname;

  const [options, setOptions] = useState<
    { key: string; value: string }[] | undefined
  >();

  if (url) {
    const repo = url.split("/");
    repoName.current = repo[repo.length - 1];
  }

  useEffect(() => {
    if (!repoName.current) {
      return;
    }

    let alreadyLoaded = false;
    let versionBuffer: { key: string; value: string }[] = [];

    setTimeout(() => {
      if (alreadyLoaded) return;
      dispatch(spi("Loading"));
    }, 100);
    (async () => {
      const softwareBuffer: any[] = await fetch(
        `https://api.github.com/repos/minebea-intec/${repoName.current}/releases`
      )
        .then((response) => response.json())
        .then((data) => {
          data.forEach((version: any, idx: number) => {
            data[idx].productName = repoName.current;
            versionBuffer?.push({
              key: version.tag_name,
              value:
                idx === 0 ? `${version.tag_name} (latest)` : version.tag_name,
            });
          });

          setOptions(versionBuffer);

          return [...data];
        })
        .catch((e) => {
          setErrorDialog(e.message);
          return [];
        });

      await loadVersionSpecificMarkdown(softwareBuffer[0]?.name || "");

      readmeMarkdown.current =
        (await fetch(
          `https://api.github.com/repos/minebea-intec/${repoName.current}/contents/README.md`
        )
          .then((response) => response.json())
          .then((data) => {
            return atob(data.content);
          })
          .catch((e) => {
            setErrorDialog(e.message);
          })) || "";

      setDisplayedMarkdown(readmeMarkdown.current);

      const dataFound = softwareBuffer.length > 0 ? true : false;

      setSoftwareProducts(dataFound ? softwareBuffer : null);

      dispatch(off());
      if (!dataFound) {
        setDisplayedMarkdown(noDataFound);
        return;
      }

      setShowDetailVersion((prevState) => ({
        show: prevState.show,
        versionText: {
          version: softwareBuffer[0].tag_name,
          versionText: versionMarkdown.current,
        },
        versions: versionBuffer.map((version) => version.value),
        markdown: versionMarkdown.current,
      }));

      alreadyLoaded = true;

      loadVersionSpecificMarkdown(softwareBuffer[0]?.name || noVersionFound);
      setTaskbarElements(taskbarElements.current);
    })();

    return () => {
      setTaskbarElements([]);
      triggerOpen(false);
    };
  }, [location.pathname]);

  useEffect(() => {
    window.addEventListener("event_download", handleTaskbarDownload);
    window.addEventListener("event_documentation", handleTaskbarDocumentation);
    window.addEventListener("event_summary", handleTableOfContents);

    return () => {
      window.removeEventListener("event_download", handleTaskbarDownload);
      window.removeEventListener(
        "event_documentation",
        handleTaskbarDocumentation
      );
      window.removeEventListener("event_summary", handleTableOfContents);
    };
  }, [displayedMarkdown, openView]);

  const handleTaskbarDownload = () => {
    setDownloadDialog(true);
  };

  const handleDownloadDialog = (e: BaseSyntheticEvent, val: string) => {
    if (e.target.value === "Cancel") {
      setDownloadDialog(false);
      return;
    }

    const index = parseInt(val.replace("|", ""), 10);

    download(softwareProducts[index]);

    setDownloadDialog(false);
  };

  const handleTaskbarDocumentation = () => {
    if (taskbarElements.current[2].text === "Documentation") {
      taskbarElements.current[2].text = "Versions";
      taskbarElements.current[3] = tableOfcontentsButton;
      setDisplayedMarkdown(readmeMarkdown.current);
      setShowDetailVersion((prevState) => ({
        ...prevState,
        show: false,
      }));
    } else {
      taskbarElements.current[2].text = "Documentation";
      taskbarElements.current[3] = { type: "empty" };
      setShowDetailVersion((prevState) => ({
        ...prevState,
        show: true,
        versionText: {
          version: prevState.versionText.version,
          versionText: versionMarkdown.current,
        },
      }));
    }

    setTaskbarElements(taskbarElements.current);
    handleTableOfContents(undefined, false);
  };

  const loadVersionSpecificMarkdown = async (version: string) => {
    version = version.replace(" (latest)", "").replace("v", "");

    const versionSha = await fetch(
      `https://api.github.com/repos/Minebea-Intec/${repoName.current}/commits?path=versionInfo.md`,
      {
        headers: {
          "X-GitHub-Api-Version": "2022-11-28",
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        const infoMdSha = data.find(
          (commit: any) =>
            commit.commit.message.includes(`${version}`) &&
            commit.commit.message.includes("versionInfo")
        );

        return infoMdSha.sha;
      })
      .catch(() => {});

    if (!versionSha) {
      versionMarkdown.current = noVersionFound;
      return;
    }

    const versionSpecificMarkdown = await fetch(
      `https://api.github.com/repos/Minebea-Intec/${repoName.current}/contents/versionInfo.md?ref=${versionSha}`,
      {
        headers: {
          "X-GitHub-Api-Version": "2022-11-28",
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        return atob(data.content);
      })
      .catch(() => {});

    versionMarkdown.current = versionSpecificMarkdown || noVersionFound;
  };

  const versionChanged = async (version: string): Promise<void> => {
    await loadVersionSpecificMarkdown(version);

    setShowDetailVersion((prevState) => ({
      ...prevState,
      versionText: {
        version: version,
        versionText: versionMarkdown.current,
      },
    }));
  };

  const download = async (software?: any) => {
    if (software) {
      const link = software.assets[0].browser_download_url;

      if (link === null) return;

      window.location.href = link;
      return;
    }

    let selectItem = (document.getElementById("select") as HTMLInputElement)
      .value;

    selectItem = selectItem.includes("latest")
      ? selectItem.replace(" (latest)", "")
      : selectItem;

    let link: string | null = "";

    for (let version in softwareProducts) {
      try {
        link =
          softwareProducts[version].tag_name === selectItem
            ? softwareProducts[version].assets[0].browser_download_url
            : null;

        if (link) {
          break;
        }
      } catch (e) {
        setErrorDialog("File not found");
      }
    }

    if (link === null) return;

    window.location.href = link;
  };

  const downloadByVersion = async (version?: any) => {
    if (version) {
      const selectedVersion = version.includes("latest")
        ? version.replace(" (latest)", "")
        : version;

      const index = softwareProducts.findIndex(
        (ver: any) => ver.tag_name === selectedVersion
      );
      const link = softwareProducts[index].assets[0].browser_download_url;

      if (link === null) return;

      window.location.href = link;
      return;
    }
  };

  const handleTableOfContents = (_event?: any, open: boolean = true) => {
    if (openView && open) {
      triggerOpen(false);
      return;
    }
    setTimeout(() => {
      const markdownContents = document.getElementById("markdown")?.childNodes;

      const headers: Array<{ node: string; text: string }> = [];

      markdownContents?.forEach((element) => {
        if (
          element.nodeName === "H1" ||
          element.nodeName === "H2" ||
          element.nodeName === "H3" ||
          element.nodeName === "H4" ||
          element.nodeName === "H5" ||
          element.nodeName === "H6"
        ) {
          headers.push({
            node: element.nodeName,
            text: element.textContent || "",
          });
        }
      });
      setData(headers);

      if (open) {
        triggerOpen(true);
      }
    }, 100);
  };

  const scroll = (targetId: string): void => {
    const element = document.getElementById(targetId);
    const rect = element?.getBoundingClientRect();
    const top = element?.offsetTop || 0;
    if (rect && element) {
      document.getElementsByClassName("hmi-inner-content")[0]?.scrollTo({
        top: top - element.clientHeight + 35, // 35px offset
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      {downloadDialog && (
        <DefaultDialog
          show={downloadDialog}
          title="Download"
          icon="icon-download"
          buttons={[
            {
              text: "Cancel",
              icon: "close",
            },
            {
              text: "Download",
              icon: "download",
            },
          ]}
          minebeaInput={[
            {
              type: "select",
              value: "XML",
              "label-left": "Software versions",
              style: { lineHeight: "unset" },
              options: options?.map((option, ia) => ({
                key: ia.toString(),
                value: option.value,
              })),
            },
          ]}
          message="Select the version you want to download."
          handleButtons={(e, val) => handleDownloadDialog(e, val || "")}
        />
      )}
      {errorDialog && (
        <ErrorDialog
          icon="icon-warning"
          show={errorDialog !== undefined}
          header="Error"
          message={errorDialog}
          handleClose={() => setErrorDialog(undefined)}
        />
      )}
      <SubHeader header="Product portfolio" />
      <div className="row">
        <div className="col hmi-inner-content">
          {softwareProducts && (
            <div className="col-12">
              <h2 className="p-2">
                <p>{softwareProducts[0]?.productName || ""}</p>
              </h2>
            </div>
          )}
          {}
          <div className="row">
            <div className="col-12">
              <div className="inner wc markdown" id="markdown">
                {!showDetailVersion.show && (
                  <MarkdownView displayedMarkdown={displayedMarkdown} />
                )}
                {showDetailVersion.show && (
                  <VersionView
                    versions={showDetailVersion.versions || []}
                    versionText={showDetailVersion.versionText}
                    getVersion={versionChanged}
                    key={showDetailVersion.versions?.length.toString()}
                    downloadVersion={(version) => downloadByVersion(version)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* {inlineTableOfContents.show && (
        <InlineTableOfContents data={inlineTableOfContents.data} />
      )} */}
    </>
  );
};

export default DownloadPage;
